import '../styles/globals.css';
import '@/components/config/i18n';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import NextApp from 'next/app';
import Head from 'next/head';
import { appWithTranslation } from 'next-i18next/pages';
import PropTypes from 'prop-types';
import { Suspense, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import { LoadingSpinner } from '@/components/Loading';
import ResponsiveAppBar from '@/components/Navigation';
import { AuthProvider } from '@/contexts/AuthContext';
import { EventProvider } from '@/contexts/EventsContext';
import { CustomThemeProvider } from '@/contexts/ThemeContext';
import { createApi } from '@/utils/api';

const muiCache = createCache({ key: 'mui', prepend: true });

function MyApp({ Component, emotionCache = muiCache, pageProps }) {
  const { initialAuth, initialEvents, ...componentPageProps } = pageProps;
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  // Spread initialAuth into AuthProvider props
  const authProps = {
    initialUser: initialAuth.user,
    initialGroups: initialAuth.groups,
    initialCsrfToken: initialAuth.csrfToken,
    initialSubmissions: initialAuth.submissions,
    initialEvents: initialAuth.events,
    initialUserData: initialAuth.userData,
    isVerified: initialAuth.isVerified,
    initialSocialAccounts: initialAuth.socialAccounts,
  };

  const eventProps = {
    initialActive: initialEvents.active,
    initialOpen: initialEvents.open,
    initialLive: initialEvents.live,
    initialSchedule: initialEvents.schedule,
  };
  if (loading) return <LoadingSpinner setIsLoading={setLoading} />;

  return (
    <Suspense fallback={null}>
      <AuthProvider {...authProps}>
        <EventProvider {...eventProps}>
          <I18nextProvider i18n={i18n}>
            <CacheProvider value={emotionCache}>
              <Head>
                <title>NoReset</title>
                {/*
                  No `maximum-scale`/`user-scalable`: blocking zoom fails WCAG 1.4.4, and iOS
                  Safari ignores it anyway. Double-tap-to-zoom on editable cells is handled by
                  `touchAction` in the Data Grid theme (see ThemeContext) instead.
                */}
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
              </Head>
              <CssBaseline />
              <CustomThemeProvider>
                <ResponsiveAppBar />
                <Component {...componentPageProps} />
              </CustomThemeProvider>
            </CacheProvider>
          </I18nextProvider>
        </EventProvider>
      </AuthProvider>
    </Suspense>
  );
}

MyApp.propTypes = {
  Component: PropTypes.func.isRequired,
  emotionCache: PropTypes.object,
  pageProps: PropTypes.shape({
    initialAuth: PropTypes.object.isRequired,
    initialEvents: PropTypes.object.isRequired,
  }).isRequired,
};

MyApp.getInitialProps = async appContext => {
  const appProps = await NextApp.getInitialProps(appContext);

  let initialAuth = {
    user: null,
    userData: null,
    groups: [],
    submissions: [],
    events: [],
    csrfToken: '',
    isVerified: false,
    socialAccounts: [],
  };

  let initialEvents = {
    active: null,
    open: [],
    live: null,
    schedule: null,
  };

  // On server, fetch auth status
  if (appContext.ctx.req) {
    const api = createApi(appContext.ctx.req.headers);
    const [statusResponse, activeResponse, openResponse, liveResponse] = await Promise.all([
      api
        .get('/api/v2/auth/status')
        // Unauthorized
        .catch(() => ({ data: {} })),
      api.get('/api/v2/events/active/'),
      api.get('/api/v2/events/open/'),
      api.get('/api/v2/events/live/'),
    ]);

    const live = liveResponse.data[0];
    const scheduleResponse = live
      ? await api
          .get(`/api/v2/schedules/by_event/${live.id}`)
          // No schedule for the live event yet
          .catch(() => null)
      : null;

    if (statusResponse.data.user) {
      initialAuth = statusResponse.data;
    }
    initialEvents = {
      active: activeResponse.data[0],
      open: openResponse.data,
      live,
      schedule: scheduleResponse?.data,
    };
  }

  return { pageProps: { ...appProps.pageProps, initialAuth, initialEvents } };
};

export default appWithTranslation(MyApp);
